import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Past AI responses for a given feature `kind`. Companion to
// useAiCall — that hook writes ai_messages, this one reads them.
// Returns messages newest-first, capped at `limit`, plus a remove()
// + refetch() pair for the history UI.
export function useAiHistory(kind, { limit = 10 } = {}) {
  const { user } = useAuth()
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tableMissing, setTableMissing] = useState(false)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user || !kind) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('ai_messages')
      .select('id, kind, text, model, usage, prompt_chars, created_at')
      .eq('user_id', user.id)
      .eq('kind', kind)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (err) {
      // Same degradation pattern the reports page uses — if
      // migration 021 hasn't run, surface a soft flag instead of
      // breaking the AI card.
      if (
        err.code === 'PGRST205'
        || /schema cache/i.test(err.message || '')
        || /does not exist/i.test(err.message || '')
      ) {
        setTableMissing(true)
      } else {
        setError(err.message)
      }
    } else {
      setMessages(data || [])
      setTableMissing(false)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, kind, limit])

  useEffect(() => { fetch() }, [fetch])

  const remove = async (id) => {
    const { error: err } = await supabase.from('ai_messages').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  return { messages, loading, error, tableMissing, remove, refetch: fetch }
}
